import React from 'react';

/**
 * Blanketterna i appens formspråk — samma dokumentkänsla som
 * årsredovisningssteget (inga ramar, generös luft, brandgröna accenter).
 * Fältkoden visas som en diskret tagg vid beloppet så att raderna mappar
 * 1:1 mot filen som lämnas in.
 *
 * Delas av alla deklarationssteg (INK2 för AB, NE för enskild firma,
 * INK4 för handelsbolag) så att blanketterna ser likadana ut oavsett
 * företagsform.
 */
const SkvStyles = () => (
    <style>{`
        .skv-sheet { max-width: 860px; }
        .skv-sheet + .skv-sheet { margin-top: 2.5rem; }
        .skv-sheet-head {
            display: flex;
            align-items: baseline;
            justify-content: space-between;
            gap: 1rem;
            flex-wrap: wrap;
            margin-bottom: 1rem;
        }
        .skv-form-chip {
            display: inline-block;
            background: var(--rgba-primary-1);
            color: var(--primary);
            font-weight: 700;
            font-size: 0.78rem;
            letter-spacing: 0.04em;
            padding: 0.2rem 0.6rem;
            border-radius: 999px;
            margin-right: 0.6rem;
        }
        .skv-form-title { font-weight: 700; font-size: 1.15rem; color: var(--title, #111827); }
        .skv-period { font-size: 0.78rem; color: #9ca3af; }
        .skv-section-title {
            font-weight: 700;
            font-size: 0.9rem;
            color: var(--title, #374151);
            margin: 1.5rem 0 0.4rem;
        }
        .skv-row {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            padding: 0.4rem 0;
            border-bottom: 1px solid #f3f4f6;
            font-size: 0.875rem;
            color: var(--title, #374151);
        }
        .skv-row-sum {
            font-weight: 700;
            border-bottom: none;
            border-top: 2px solid #374151;
            padding-top: 0.5rem;
        }
        .skv-row-strong { font-weight: 700; }
        .skv-punkt {
            flex: 0 0 3.2rem;
            font-weight: 600;
            color: #9ca3af;
            font-variant-numeric: tabular-nums;
            font-size: 0.8rem;
        }
        .skv-label { flex: 1; min-width: 0; }
        .skv-sida { color: #9ca3af; font-size: 0.75rem; }
        .skv-box {
            flex: 0 0 auto;
            display: inline-flex;
            align-items: center;
            gap: 0.5rem;
            min-width: 11rem;
            justify-content: flex-end;
        }
        .skv-kod {
            display: inline-flex;
            align-items: center;
            padding: 0.1rem 0.45rem;
            font-size: 0.68rem;
            color: #6b7280;
            background: #f3f4f6;
            border-radius: 999px;
            font-variant-numeric: tabular-nums;
        }
        .skv-belopp {
            text-align: right;
            min-width: 6.5rem;
            font-variant-numeric: tabular-nums;
            white-space: nowrap;
            font-weight: 600;
        }
        .skv-box.empty .skv-belopp::after { content: "—"; color: #d1d5db; font-weight: 400; }
        .skv-note { font-size: 0.78rem; color: #9ca3af; margin: 0.75rem 0 0; max-width: 68ch; }
        .skv-question {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 1rem;
            padding: 0.5rem 0;
            border-bottom: 1px solid #f3f4f6;
            font-size: 0.875rem;
            color: var(--title, #374151);
        }
        .skv-inline-input {
            width: 9rem;
            flex-shrink: 0;
            text-align: right;
            font-size: 0.875rem;
            font-weight: 600;
            font-variant-numeric: tabular-nums;
            color: var(--title, #374151);
            padding: 0.3rem 0.6rem;
            border: 1px solid #e5e7eb;
            border-radius: 0.5rem;
            background: #fff;
            transition: border-color 0.15s ease;
        }
        .skv-inline-input:focus {
            outline: none;
            border-color: var(--primary);
        }
        .skv-inline-input::placeholder { color: #d1d5db; font-weight: 400; }
        .skv-seg {
            display: inline-flex;
            flex-shrink: 0;
            border: 1px solid #e5e7eb;
            border-radius: 999px;
            overflow: hidden;
            background: #fff;
        }
        .skv-seg button {
            border: none;
            background: transparent;
            padding: 0.24rem 0.95rem;
            font-size: 0.8125rem;
            font-weight: 600;
            color: #9ca3af;
            cursor: pointer;
            transition: background 0.15s ease, color 0.15s ease;
        }
        .skv-seg button + button { border-left: 1px solid #e5e7eb; }
        .skv-seg button:hover { color: var(--primary); }
        .skv-seg button.active { background: var(--primary); color: #fff; }
        @media (max-width: 640px) {
            .skv-row { flex-wrap: wrap; }
            .skv-box { min-width: 9rem; }
        }
    `}</style>
);

/** Ja/Nej som blankettkryss — samma segmentstil som intro-kortets SegToggle. */
export const SkvSeg = ({ value, onChange, name }) => (
    <span className="skv-seg" role="group" aria-label={name}>
        <button
            type="button"
            className={value === true ? 'active' : ''}
            aria-pressed={value === true}
            onClick={() => onChange(true)}
        >
            Ja
        </button>
        <button
            type="button"
            className={value === false ? 'active' : ''}
            aria-pressed={value === false}
            onClick={() => onChange(false)}
        >
            Nej
        </button>
    </span>
);

export default SkvStyles;
